import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { LayoutObjectType } from '@yard/app/api/model'
import { observer } from 'mobx-react-lite'
import { useYardSetupMessageHandler } from '../../hooks/useYardSetupMessageHandler'
import { ILayoutObjectFormData } from '../../models/yard-setup.model'
import { LayoutObjectViewStore } from '../../stores/nodes/layout-object.view-store'
import { LayoutObjectForm } from '../molecules/LayoutObjectForm'

interface Props {
  layoutObjectViewStore: LayoutObjectViewStore
}

export const LayoutObjectDialogs = observer(({ layoutObjectViewStore }: Props) => {
  const { t } = useTranslate()
  const {
    getExceptionMessage,
    getLayoutObjectSuccessMessage,
    getLayoutObjectDeleteSuccessMessage,
    getGateSuccessMessage,
    getGateDeleteSuccessMessage,
    getHoldingPointSuccessMessage,
    getHoldingPointDeleteSuccessMessage,
    getHandoverAreaSuccessMessage,
    getHandoverAreaDeleteSuccessMessage,
  } = useYardSetupMessageHandler()

  const getSuccessMessage = (isEditing: boolean) => {
    switch (layoutObjectViewStore.editingType) {
      case LayoutObjectType.Gate:
        return getGateSuccessMessage(isEditing)
      case LayoutObjectType.HoldingPoint:
        return getHoldingPointSuccessMessage(isEditing)
      case LayoutObjectType.HandoverArea:
        return getHandoverAreaSuccessMessage(isEditing)
      default:
        return getLayoutObjectSuccessMessage(isEditing)
    }
  }

  const getDeleteSuccessMessage = () => {
    switch (layoutObjectViewStore.focusedLayoutObject?.type) {
      case LayoutObjectType.Gate:
        return getGateDeleteSuccessMessage()
      case LayoutObjectType.HoldingPoint:
        return getHoldingPointDeleteSuccessMessage()
      case LayoutObjectType.HandoverArea:
        return getHandoverAreaDeleteSuccessMessage()
      default:
        return getLayoutObjectDeleteSuccessMessage()
    }
  }

  const onSubmit = async (data: ILayoutObjectFormData) => {
    try {
      await layoutObjectViewStore.submit(data)
      appStore.setShowAlert('success', getSuccessMessage(!!layoutObjectViewStore.editingId))
      layoutObjectViewStore.closeFormDialog()
    } catch (e: any) {
      appStore.setShowAlert('error', getExceptionMessage(e))
    }
  }

  const onDelete = async () => {
    try {
      await layoutObjectViewStore.delete(layoutObjectViewStore.editingId!)
      appStore.setShowAlert('success', getDeleteSuccessMessage())
      layoutObjectViewStore.setDeleteDialogOpen()
    } catch (e: any) {
      appStore.setShowAlert('error', getExceptionMessage(e))
    }
  }

  const getFormDialogTitle = () => {
    const isEditing = !!layoutObjectViewStore.focusedLayoutObject
    switch (layoutObjectViewStore.editingType) {
      case LayoutObjectType.Gate:
        return isEditing ? t('editGate', 'Edit gate') : t('addGate', 'Add gate')
      case LayoutObjectType.HoldingPoint:
        return isEditing
          ? t('editHoldingPoint', 'Edit holding point')
          : t('addHoldingPoint', 'Add holding point')
      case LayoutObjectType.HandoverArea:
        return isEditing
          ? t('editHandoverArea', 'Edit handover area')
          : t('addHandoverArea', 'Add handover area')
      default:
        return isEditing
          ? t('editLayoutObject', 'Edit layout object')
          : t('addLayoutObject', 'Add layout object')
    }
  }

  const getDeleteDialogTitle = () => {
    switch (layoutObjectViewStore.focusedLayoutObject?.type) {
      case LayoutObjectType.Gate:
        return t('deleteGate', 'Delete gate')
      case LayoutObjectType.HoldingPoint:
        return t('deleteHoldingPoint', 'Delete holding point')
      case LayoutObjectType.HandoverArea:
        return t('deleteHandoverArea', 'Delete handover area')
      default:
        return t('deleteLayoutObject', 'Delete layout object')
    }
  }

  return (
    <>
      <ConfirmationDialog
        open={layoutObjectViewStore.isFormDialogOpen}
        title={getFormDialogTitle()}
        primaryActionText={
          layoutObjectViewStore.focusedLayoutObject
            ? t('yesUpdate', 'Yes, update')
            : t('yesCreate', 'Yes, create')
        }
        closeLabel={t('noCancel', 'No, cancel')}
        formId='layout-object-form'
        maxWidth='sm'
        onClose={() => layoutObjectViewStore.closeFormDialog()}
      >
        <LayoutObjectForm
          id='layout-object-form'
          layoutObjectViewStore={layoutObjectViewStore}
          onSubmit={onSubmit}
        />
      </ConfirmationDialog>

      <ConfirmationDialog
        open={layoutObjectViewStore.isDeleteDialogOpen}
        title={getDeleteDialogTitle()}
        message={t('youCannotUndoThisAction', 'You cannot undo this action.')}
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('noCancel', 'No, cancel')}
        onClose={() => layoutObjectViewStore.setDeleteDialogOpen()}
        onConfirm={onDelete}
        maxWidth='xs'
      />
    </>
  )
})
